import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { authService } from "../services/authService";
import { useAuthStore } from "../stores/authStore";
import { useBottomNotificationStore } from "../stores/bottomNotificationStore";

// Query keys
export const authKeys = {
  all: ["auth"],
  user: () => [...authKeys.all, "user"],
  check: () => [...authKeys.all, "check"],
};

// Login mutation
export const useLogin = () => {
  const navigate = useNavigate();
  const { login: loginStore, setLoading } = useAuthStore();
  const showNotification = useBottomNotificationStore((s) => s.showNotification);

  return useMutation(authService.login, {
    onMutate: () => {
      setLoading(true);
    },
    onSuccess: async (data) => {
      try {
        const result = await loginStore(data);
        if (result.success) {
          showNotification("Login successful", "success");

          // Check if password reset is required
          if (data.requiresPasswordReset) {
            navigate("/change-password");
          } else {
            navigate("/dashboard");
          }
        } else {
          showNotification(result.error || "Login failed", "error");
        }
      } catch (error) {
        showNotification(error.message || "Login failed", "error");
      }
    },
    onError: (error) => {
      setLoading(false);
    },
  });
};

// Register mutation
export const useRegister = () => {
  const navigate = useNavigate();
  const showNotification = useBottomNotificationStore((s) => s.showNotification);

  return useMutation(authService.register, {
    onSuccess: () => {
      showNotification(
        "Registration successful! Please check your email to verify your account.",
        "success"
      );
      navigate("/sign-in");
    },
    onError: (error) => {
      showNotification(error.message || "Registration failed", "error");
    },
  });
};

// Logout mutation
export const useLogout = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { logout: logoutStore } = useAuthStore();
  const showNotification = useBottomNotificationStore((s) => s.showNotification);

  return useMutation(authService.logout, {
    onSuccess: () => {
      // Clear all cached data
      queryClient.clear();
      // Update store
      logoutStore();
      showNotification("Logged out successfully", "success");
      navigate("/sign-in");
    },
    onError: (error) => {
      // Even if API call fails, logout locally
      queryClient.clear();
      logoutStore();
      navigate("/sign-in");
      showNotification(error.message || "Logout failed", "error");
    },
  });
};

// Forgot password mutation
export const useForgotPassword = () => {
  const showNotification = useBottomNotificationStore((s) => s.showNotification);

  return useMutation(authService.forgotPassword, {
    onSuccess: () => {
      showNotification(
        "Password reset email sent! Please check your inbox.",
        "success"
      );
    },
    onError: (error) => {
      showNotification(error.message || "Failed to send reset email", "error");
    },
  });
};

// Reset password mutation
export const useResetPassword = () => {
  const navigate = useNavigate();
  const showNotification = useBottomNotificationStore((s) => s.showNotification);

  return useMutation(
    ({ token, password }) => authService.resetPassword(token, password),
    {
      onSuccess: () => {
        showNotification(
          "Password reset successful! Please login with your new password.",
          "success"
        );
        navigate("/sign-in");
      },
      onError: (error) => {
        showNotification(error.message || "Password reset failed", "error");
      },
    }
  );
};

// Change password mutation
export const useChangePassword = () => {
  const showNotification = useBottomNotificationStore((s) => s.showNotification);

  return useMutation(
    ({ currentPassword, newPassword }) =>
      authService.changePassword(currentPassword, newPassword),
    {
      onSuccess: () => {
        showNotification("Password changed successfully", "success");
      },
      onError: (error) => {
        showNotification(
          error.message || "Failed to change password",
          "error"
        );
      },
    }
  );
};

// Get current user query using whoAmI
export const useCurrentUser = () => {
  const { isAuthenticated } = useAuthStore();

  return useQuery(authKeys.user(), authService.whoAmI, {
    enabled: isAuthenticated,
    retry: false,
    onError: (error) => {
      if (error.status === 401) {
        useAuthStore.getState().logout();
      }
    },
  });
};

// Check authentication status using whoAmI
export const useAuthCheck = () => {
  const { isAuthenticated, logout } = useAuthStore();

  return useQuery(authKeys.check(), authService.whoAmI, {
    enabled: isAuthenticated,
    retry: false,
    refetchOnWindowFocus: false,
    onError: (error) => {
      if (error.status === 401) {
        logout();
      }
    },
  });
};

// Hook specifically for whoAmI functionality
export const useWhoAmI = () => {
  const { isAuthenticated } = useAuthStore();

  return useQuery(["whoAmI"], authService.whoAmI, {
    enabled: isAuthenticated,
    retry: false,
    onError: (error) => {
      if (error.status === 401) {
        useAuthStore.getState().logout();
      }
    },
  });
};

// Verify email mutation
export const useVerifyEmail = () => {
  const navigate = useNavigate();
  const showNotification = useBottomNotificationStore((s) => s.showNotification);

  return useMutation(authService.verifyEmail, {
    onSuccess: () => {
      showNotification(
        "Email verified successfully! You can now login.",
        "success"
      );
      navigate("/sign-in");
    },
    onError: (error) => {
      showNotification(error.message || "Email verification failed", "error");
    },
  });
};
